/** Styled components for PromptEditor (split out of PromptEditor.jsx, #1099). */
import styled from '@emotion/styled';

export const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const Header = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

export const TitleSection = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 4px 0;
`;

export const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  color: #666;
`;

export const ModuleBadge = styled.span`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(99, 102, 241, 0.15);
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.25);
`;

export const MetaKey = styled.code`
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 11px;
  color: #555;
`;

export const DescriptionField = styled.label`
  display: block;
  margin-top: 4px;
`;

export const DescriptionLabel = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const DescriptionTextarea = styled.textarea`
  width: 100%;
  min-height: 88px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ccc;
  font-size: 13px;
  line-height: 1.45;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.45);
  }
  &::placeholder { color: #555; }
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
`;

export const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: ${p => p.disabled ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.8)'};
  color: ${p => p.disabled ? '#666' : '#fff'};
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.15s;
  &:hover:not(:disabled) { background: rgba(99, 102, 241, 1); }
`;

export const ResetButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  color: #888;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: rgba(255, 255, 255, 0.2); color: #ccc; }
`;

export const ConfigBar = styled.div`
  display: flex;
  gap: 16px;
  padding: 12px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
  align-items: center;
  flex-wrap: wrap;
`;

export const ConfigGroup = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #888;
`;

export const ConfigSelect = styled.select`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #fff;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); }
`;

export const ConfigInput = styled.input`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #fff;
  padding: 4px 8px;
  font-size: 12px;
  width: ${p => p.$width || '70px'};
  &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); }
`;

export const VariableChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-left: auto;
`;

export const Chip = styled.span`
  padding: 2px 8px;
  background: rgba(245, 158, 11, 0.15);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 4px;
  font-size: 11px;
  color: #f59e0b;
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
`;

export const EditArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const PromptSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: ${p => p.$minHeight || '120px'};
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: #e0e0e0;
  font-family: 'JetBrains Mono', 'SF Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.6;
  padding: 16px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.1);
  }
  &::placeholder { color: #444; }
`;

export const ChangeNoteInput = styled.input`
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #e0e0e0;
  font-size: 13px;
  padding: 10px 14px;
  width: 100%;
  &:focus { outline: none; border-color: rgba(99, 102, 241, 0.5); }
  &::placeholder { color: #444; }
`;

export const VersionBadge = styled.span`
  font-size: 11px;
  color: #6366f1;
  padding: 2px 8px;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 4px;
`;

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #555;
  gap: 12px;
`;

export const TooltipAnchor = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: help;
  color: #555;
  &:hover { color: #888; }
`;

export const TooltipPortalBubble = styled.span`
  position: fixed;
  background: #1e1e2e;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 400;
  color: #ccc;
  white-space: nowrap;
  pointer-events: none;
  z-index: 99999;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  transform: translateX(-50%);
`;

export const WarningBanner = styled.div`
  margin: 0 0 4px 0;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 10px;
  overflow: hidden;
`;

export const WarningHeader = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 14px;
  background: none;
  border: none;
  color: #f59e0b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  &:hover { background: rgba(245, 158, 11, 0.05); }
`;

export const WarningBody = styled.div`
  padding: 0 14px 12px 36px;
  font-size: 12px;
  line-height: 1.6;
  color: #b0a080;
  ul { margin: 4px 0 0 0; padding-left: 16px; }
  li { margin-bottom: 4px; }
  strong { color: #d4a94a; }
`;
